/**
 * My Bag API client (MY_BAG_PLAN.md §5, §2.3, §3.5).
 *
 * Same axios + JWT-interceptor shape as `postFittingReviewService.ts`. Every
 * route below is superuser-gated at the router (§7.3), so a 403 here means the
 * gate, not a bug.
 */
import axios from 'axios';

import {
  Bag,
  BagAnalysis,
  BagAnalyzeRequest,
  BagCreate,
  BagSeedResponse,
  BagSlot,
  BagSlotUpdate,
  CatalogItem,
  CatalogTable,
  MeasuredCarriesResponse,
} from '../types/bag.types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const storageType = localStorage.getItem('auth_storage_type');
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    const token = storage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * The active bag, or `null` when the user has no bag yet.
 *
 * "No bag yet" is a steady state, not an error — the API returns `null` at 200
 * rather than 404, so callers must handle null and must not treat it as a
 * failure.
 */
export const getMyBag = async (): Promise<Bag | null> => {
  const { data } = await api.get<Bag | null>('/bag');
  return data ?? null;
};

export const createBag = async (payload: BagCreate): Promise<Bag> => {
  const { data } = await api.post<Bag>('/bag', payload);
  return data;
};

export const getBag = async (bagId: number): Promise<Bag> => {
  const { data } = await api.get<Bag>(`/bag/${bagId}`);
  return data;
};

export const updateBag = async (
  bagId: number,
  payload: Partial<BagCreate>
): Promise<Bag> => {
  const { data } = await api.patch<Bag>(`/bag/${bagId}`, payload);
  return data;
};

export const deleteBag = async (bagId: number): Promise<void> => {
  await api.delete(`/bag/${bagId}`);
};

export const createSlot = async (bagId: number, slot: BagSlot): Promise<BagSlot> => {
  const { data } = await api.post<BagSlot>(`/bag/${bagId}/slots`, slot);
  return data;
};

/**
 * Replace the whole ladder in one transaction.
 *
 * This is the write the seed-and-tap-confirm flow uses: it fills fourteen rungs
 * at once, and the frontend should not have to diff them one by one.
 */
export const replaceSlots = async (
  bagId: number,
  slots: BagSlot[]
): Promise<BagSlot[]> => {
  const { data } = await api.put<BagSlot[]>(`/bag/${bagId}/slots`, slots);
  return data;
};

export const updateSlot = async (
  bagId: number,
  slotId: number,
  patch: BagSlotUpdate
): Promise<BagSlot> => {
  const { data } = await api.patch<BagSlot>(`/bag/${bagId}/slots/${slotId}`, patch);
  return data;
};

export const deleteSlot = async (bagId: number, slotId: number): Promise<void> => {
  await api.delete(`/bag/${bagId}/slots/${slotId}`);
};

/**
 * A *proposed* ladder seeded from the user's own profile. Persists nothing —
 * the user tap-confirms and `replaceSlots` is what writes it (§2.3).
 *
 * `seedable: false` is a normal answer, not an error: it means the profile is
 * missing one of the two anchors (driver carry and 7-iron carry), and `reason`
 * says which.
 */
export const seedBag = async (): Promise<BagSeedResponse> => {
  const { data } = await api.post<BagSeedResponse>('/bag/seed', {});
  return data;
};

/**
 * The user's own launch-monitor carries, median per club (§2.4). A proposal
 * like the seed: it persists nothing, and `replaceSlots` is still the write.
 *
 * An empty `carries` with a non-zero `shots_considered` is the interesting
 * case — shots on file, but fewer than `min_shots` of any single club, so
 * nothing is honest enough to call measured yet.
 */
export const getMeasuredCarries = async (): Promise<MeasuredCarriesResponse> => {
  const { data } = await api.get<MeasuredCarriesResponse>('/bag/measured-carries');
  return data;
};

/**
 * Stateless analysis — nothing is stored, so this is safe to call on every
 * edit. Passing `slots` analyzes an **unsaved** ladder; passing only `bag_id`
 * analyzes what is persisted.
 */
export const analyzeBag = async (payload: BagAnalyzeRequest): Promise<BagAnalysis> => {
  const { data } = await api.post<BagAnalysis>('/bag/analyze', payload);
  return data;
};

/**
 * Catalog search for the Rack's iron and wedge pickers. Woods and hybrids have
 * no catalog at all (§9.4) — those slots are free text, and calling this for
 * them is a mistake, not an empty result.
 */
export const searchCatalog = async (
  table: CatalogTable,
  params: { manufacturer?: string; model?: string; limit?: number } = {}
): Promise<CatalogItem[]> => {
  const { data } = await api.get<{ equipment?: CatalogItem[] }>(`/${table}/`, {
    params: { limit: 50, ...params },
  });
  return data?.equipment ?? [];
};

/**
 * Distinct brands, for the picker's first step. Served by the existing
 * `/equipment/brands/{club_type}` route, which is already sorted — deriving
 * them from a page of `searchCatalog` would silently show only the brands that
 * happened to fall inside the limit.
 */
export const getCatalogBrands = async (table: CatalogTable): Promise<string[]> => {
  const clubType = table === 'irons' ? 'iron' : 'wedge';
  const { data } = await api.get<{ brands?: string[] }>(`/equipment/brands/${clubType}`);
  return data?.brands ?? [];
};
